import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Table, Tabs, Button, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { supabase } from '../lib/supabase';
import type { WmsManualOrder } from '../types/wms';
import { ManualOrderDetailModal } from './PicqerOrders/ManualOrderDetailModal';
import { AdminNavBar } from '../components/AdminNavBar';

const PENDING_STATUSES = ['awaiting_quote', 'quote_ready', 'pending_customer_approval'];

const PicqerManualOrdersAdmin: React.FC = () => {
  const [orders, setOrders] = useState<WmsManualOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');
  const [detailOrder, setDetailOrder] = useState<WmsManualOrder | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('wms_manual_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders((data ?? []) as WmsManualOrder[]);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const openDetail = (order: WmsManualOrder) => {
    setDetailOrder(order);
    setDetailOpen(true);
  };

  const filteredOrders = activeTab === 'pending'
    ? orders.filter((o) => PENDING_STATUSES.includes(o.status))
    : orders;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id: number) => `#${id}`,
    },
    {
      title: 'Company ID',
      dataIndex: 'company_id',
      key: 'company_id',
      width: 90,
    },
    {
      title: 'WMS',
      dataIndex: 'wms_provider',
      key: 'wms_provider',
      width: 90,
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      ellipsis: true,
    },
    {
      title: 'Delivery',
      key: 'delivery',
      render: (_: unknown, r: WmsManualOrder) =>
        [r.deliveryname, r.deliverycity, r.deliverycountry].filter(Boolean).join(', ') || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
    },
    {
      title: 'Size',
      dataIndex: 'shipment_size',
      key: 'shipment_size',
      width: 80,
    },
    {
      title: 'Quote',
      key: 'quote',
      width: 100,
      render: (_: unknown, r: WmsManualOrder) =>
        r.admin_quote_amount != null ? `€${Number(r.admin_quote_amount).toFixed(2)}` : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, r: WmsManualOrder) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>
          Open
        </Button>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (t: string) => new Date(t).toLocaleString(),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />
      <Typography.Title level={2}>WMS manual orders</Typography.Title>
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as 'pending' | 'all')}
        items={[
          { key: 'pending', label: 'Pending' },
          { key: 'all', label: 'All' },
        ]}
      />
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredOrders}
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
      />
      <ManualOrderDetailModal
        order={detailOrder}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailOrder(null); }}
        onQuoteSubmitted={fetchOrders}
      />
    </div>
  );
};

export default PicqerManualOrdersAdmin;
